import {
  useAccountTitleOptions,
  useAccountingTypes,
  useMinistries,
} from '@/features/master-table/hooks';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import {
  ActivityFlowSection,
  BasicInfoSection,
  InspectionAndImprovementSection,
  LedgerPage,
  PaymentSection,
  RelatedMaterialSection,
} from '@/features/mold/components';
import { BudgetAndExecutionSection } from '@/features/mold/components/pdf/rs-ledger/budget-and-execution-section/BudgetAndExecutionSection';
import {
  PAPER_FILE_SIZE,
  getConcatenatedBusinessManagerName,
  getMinistryName,
} from '@/features/mold/utils';
import {
  useActivityTreeOptions,
  useNestedConsecutiveIndicators,
} from '@/features/project/_hooks/activity';
import { useBusinessManagers } from '@/features/project/_hooks/business-managers';
import {
  useConsecutiveBudgets,
  useConsecutiveExecutions,
  useConsecutiveFiscalYears,
  useConsecutiveRequestAmounts,
} from '@/features/project/_hooks/consecutive';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import { useContractorPayments } from '@/features/project/_hooks/payment';
import { usePaymentGraph } from '@/features/project/_hooks/payment-graph';
import { usePaymentGroups } from '@/features/project/_hooks/payment-group';
import { useProject } from '@/features/project/_hooks/project';
import { useRelatedMaterials } from '@/features/project/_hooks/related-material';
import { useId } from '@/hooks';
import { uniq } from 'lodash-es';

export const RsProjectLedgerPage = () => {
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId,
    params: {
      showMigrated: true,
      historySavedAt: project.data?.historySavedAt,
    },
    enabled: !!projectId,
  });

  const policies = usePolicies({
    params: { referenceFiscalYear: project.data?.fiscalYear.toString() },
    enabled: !!project.data?.fiscalYear,
  });

  const consecutiveFiscalYears = useConsecutiveFiscalYears({ id: projectId });
  const consecutiveRequestAmounts = useConsecutiveRequestAmounts(projectId);
  const consecutiveBudgets = useConsecutiveBudgets(projectId);
  const consecutiveExecutions = useConsecutiveExecutions(projectId);

  const ministries = useMinistries();
  const concatenatedBusinessManagerName = useBusinessManagers({
    projectId,
    select: getConcatenatedBusinessManagerName,
  });
  const relatedMaterials = useRelatedMaterials(projectId);
  const paymentGroups = usePaymentGroups({ projectId });
  const paymentGraph = usePaymentGraph(projectId);
  const payments = useContractorPayments({ projectId });
  const accountingTypes = useAccountingTypes();
  const accountTitleOptions = useAccountTitleOptions();

  const activityTreeOption = useActivityTreeOptions(projectId);
  const routeIndicatorIds = uniq(
    activityTreeOption.selectOptions.flatMap((option) =>
      option.routeIndicators.flatMap((v) => v.indicator.id),
    ),
  );

  const nestedConsecutiveIndicators =
    useNestedConsecutiveIndicators(routeIndicatorIds);

  const ministryName = getMinistryName({
    targetId: project.data?.ministryId,
    ministries: ministries.data,
  });

  const isLoading =
    project.isLoading ||
    ministries.isLoading ||
    projectOrganizations.isLoading ||
    concatenatedBusinessManagerName.isLoading ||
    policies.isLoading ||
    relatedMaterials.isLoading ||
    paymentGroups.isLoading ||
    payments.isLoading ||
    paymentGraph.isLoading ||
    accountingTypes.isLoading ||
    accountTitleOptions.isLoading ||
    consecutiveFiscalYears.isLoading ||
    consecutiveRequestAmounts.isLoading ||
    consecutiveBudgets.isLoading ||
    consecutiveExecutions.isLoading;

  return (
    <LedgerPage width={PAPER_FILE_SIZE.A4.height} isLoading={isLoading}>
      <BasicInfoSection
        project={project.data}
        ministryName={ministryName}
        organizations={projectOrganizations.data}
        concatenatedBusinessManagerName={concatenatedBusinessManagerName.data}
        policies={policies.data}
      />
      <BudgetAndExecutionSection
        project={project.data}
        consecutiveFiscalYears={consecutiveFiscalYears.data}
        consecutiveRequestAmounts={consecutiveRequestAmounts.data}
        consecutiveBudgets={consecutiveBudgets.data}
        consecutiveExecutions={consecutiveExecutions.data}
      />
      <ActivityFlowSection
        project={project.data}
        nestedConsecutiveIndicators={nestedConsecutiveIndicators.data}
        tree={activityTreeOption.tree.data}
        selectOptions={activityTreeOption.selectOptions}
      />
      <InspectionAndImprovementSection
        project={project.data}
        accountTitleOptions={accountTitleOptions.data}
        accountingTypes={accountingTypes.data}
      />
      <PaymentSection
        project={project.data}
        paymentGraph={paymentGraph.data}
        paymentGroups={paymentGroups.data}
        payments={payments.data}
      />
      <RelatedMaterialSection relatedMaterials={relatedMaterials.data} />
    </LedgerPage>
  );
};
